import React, { useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Vimeo-Video austauschen: nur diese Werte anpassen.
//
// VIMEO_ID    – die Zahl aus der Vimeo-URL
// VIMEO_HASH  – der "h="-Parameter bei nicht gelisteten Videos (sonst "")
// VIDEO_RATIO – Seitenverhältnis der Quelldatei als Breite/Höhe
// BAND_RATIO  – Seitenverhältnis des Bands auf der Seite (Breite/Höhe)
// POSTER      – Standbild, das sofort sichtbar ist, bis das Video läuft.
//               Neues holen: https://vimeo.com/api/oembed.json?url=<video-url>
//               liefert "thumbnail_url".
//
// Beispiel: https://vimeo.com/1175654242?h=8d55abfc7a
//                          ^ VIMEO_ID    ^ VIMEO_HASH
//
// Solange die Quelle hochkant ist (9/16), wird sie formatfüllend skaliert und
// oben/unten beschnitten – sonst stünde sie als schmaler Streifen zwischen
// zwei schwarzen Flächen. Sobald die Querformat-Fassung hochgeladen ist:
// VIDEO_RATIO auf 16 / 9 setzen. Der Beschnitt wird dann deutlich kleiner,
// und bei BAND_RATIO = 16 / 9 verschwindet er ganz.
// ---------------------------------------------------------------------------
const VIMEO_ID = "1175654242";
const VIMEO_HASH = "8d55abfc7a";
const VIDEO_RATIO = 9 / 16;
const BAND_RATIO = 21 / 9;
// Absolut, weil der Pfad sonst gegen die aktuelle URL aufgelöst wird und
// unter einer Adresse mit Schrägstrich am Ende ins Leere zeigt.
const POSTER = "/img/video-poster.jpg";

// Startqualität. Ohne diese Angabe wählt Vimeo automatisch 1080p, weil der
// Iframe durch die formatfüllende Skalierung sehr groß ist (auf dem Desktop
// rund 1440x2559 px) – gemessen dauert der Start dadurch etwa 4 Sekunden.
// Mit 720p sind es rund 2 Sekunden. Für den stummen Hintergrundfilm ist das
// der bessere Kompromiss; auf "1080p" ändern, wenn Schärfe wichtiger ist.
const QUALITY = "720p";

// So lange bekommt das automatische Abspielen Zeit. Gemessen startet der
// Film nach rund 2 Sekunden, bei zunächst blockiertem Autoplay auch mal
// erst nach 4,8 Sekunden. Der Wert liegt bewusst darüber, damit der
// Abspielknopf nicht kurz aufblitzt, wenn die Wiedergabe doch noch
// anläuft. Bleibt sie aus (iOS im Energiesparmodus), erscheint er – das
// Standbild ist die ganze Zeit sichtbar, es wirkt also nichts kaputt.
const AUTOPLAY_GRACE_MS = 6500;

// Nach einem Tippen auf den Abspielknopf: falls Vimeos Rückmeldung
// ausbleibt (blockierte Drittanbieter-Frames), wird der Player trotzdem
// eingeblendet, statt dauerhaft unsichtbar zu bleiben.
const REVEAL_FALLBACK_MS = 1200;

// Bei Beschnitt sind die Vimeo-Bedienelemente außerhalb des sichtbaren
// Bereichs. Deshalb läuft der Film dann im Hintergrund-Modus: stumm, in
// Schleife, ohne Steuerung. Ohne Beschnitt bekommt er die normale Steuerung.
const isCropped = Math.abs(VIDEO_RATIO - BAND_RATIO) > 0.01;

const params = new URLSearchParams({
  ...(VIMEO_HASH ? { h: VIMEO_HASH } : {}),
  badge: "0",
  autopause: "0",
  quality: QUALITY,
  ...(isCropped ? { background: "1" } : {}),
});

const PLAYER_ORIGIN = "https://player.vimeo.com";
const src = `${PLAYER_ORIGIN}/video/${VIMEO_ID}?${params}`;

// Formatfüllend einpassen ("cover"): die Achse, die sonst zu kurz käme, wird
// über 100 % hinaus vergrößert, der Überstand ist weggeschnitten. Auf dem
// Handy ist das Band steiler (siehe App.css) – die hier berechnete Höhe
// deckt das mit ab, dort wird lediglich etwas mehr beschnitten.
const coverStyle =
  VIDEO_RATIO < BAND_RATIO
    ? { width: "100%", height: `${(BAND_RATIO / VIDEO_RATIO) * 100}%` }
    : { height: "100%", width: `${(VIDEO_RATIO / BAND_RATIO) * 100}%` };

export const VideoSection = () => {
  const iframeRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [showButton, setShowButton] = useState(false);
  // Spiegelt "playing" für den Timer unten. Der Effekt läuft nur einmal,
  // sein Closure würde sonst dauerhaft den Startwert false sehen.
  const playingRef = useRef(false);

  const post = (message) => {
    const win = iframeRef.current && iframeRef.current.contentWindow;
    if (win) win.postMessage(JSON.stringify(message), PLAYER_ORIGIN);
  };

  // Vimeo meldet über postMessage, wann die Wiedergabe wirklich beginnt.
  // Erst dann wird der Player über das Standbild geblendet – vorher würde
  // man nur die schwarze Player-Fläche sehen.
  useEffect(() => {
    // Beim Prerendering (scripts/prerender.mjs) läuft kein Player. Ohne diese
    // Abkürzung landete der Abspielknopf je nach Timing im statischen HTML,
    // obwohl der erste Render im Browser ihn nie zeigt – React meldet das
    // beim Hydrieren als Fehler.
    if (typeof window !== "undefined" && window.__PRERENDER__) return;

    const subscribe = () => {
      const win = iframeRef.current && iframeRef.current.contentWindow;
      if (!win) return;
      ["play", "playing"].forEach((value) =>
        win.postMessage(
          JSON.stringify({ method: "addEventListener", value }),
          PLAYER_ORIGIN
        )
      );
    };

    const onMessage = (event) => {
      if (event.origin !== PLAYER_ORIGIN) return;
      let data;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch (err) {
        return;
      }
      if (!data) return;
      // Vimeo nimmt Registrierungen erst nach "ready" entgegen.
      if (data.event === "ready") subscribe();
      if (data.event === "play" || data.event === "playing") {
        playingRef.current = true;
        setPlaying(true);
        setShowButton(false);
      }
    };

    window.addEventListener("message", onMessage);

    // Handys blockieren automatisches Abspielen (iOS generell im
    // Energiesparmodus, Android je nach Einstellung). Läuft der Film bis
    // dahin nicht, bekommt der Nutzer einen Abspielknopf statt einer
    // schwarzen Fläche.
    const timer = setTimeout(() => {
      if (!playingRef.current) setShowButton(true);
    }, AUTOPLAY_GRACE_MS);

    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
    };
  }, []);

  const handlePlay = () => {
    post({ method: "play" });
    setShowButton(false);
    // Falls die Rückmeldung ausbleibt (blockierte Drittanbieter-Frames),
    // wird der Player nach kurzer Zeit trotzdem eingeblendet.
    setTimeout(() => {
      playingRef.current = true;
      setPlaying(true);
    }, REVEAL_FALLBACK_MS);
  };

  return (
    <section
      id="video"
      className="video-section"
      style={{ "--band-ratio": BAND_RATIO }}
      aria-label="Projektfilm"
    >
      <div
        className={`video-frame${playing ? " is-playing" : ""}`}
        style={{ backgroundImage: `url(${POSTER})` }}
      >
        {showButton && (
          <button
            type="button"
            className="video-play"
            onClick={handlePlay}
            aria-label="Film abspielen"
          >
            <span className="video-play-icon"></span>
          </button>
        )}
        <iframe
          ref={iframeRef}
          src={src}
          style={coverStyle}
          title="Merdovic Trockenbau – Projektfilm"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        ></iframe>
      </div>
    </section>
  );
};
